import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "components/Modal";
import Button from "components/Button";
import clsx from "clsx";
import paymentService from "api/payment/payment.service";
import { IPaymentAddonItem, IPaymentAddonsResponse } from "api/payment/payment.type";
import { useSnackbar } from "hooks/useSnackbar";
import { formatDate, DateFormat } from "utils";
import { trackEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";

interface BuyCreditsModalProps {
  show: boolean;
  onClose: () => void;
  addons: IPaymentAddonsResponse;
}

// One-time extra credit packages (REF-5450). Self-contained on purpose: data comes in via
// props so a future entry point (e.g. the 402 paywall) can reuse it with its own fetch.
const BuyCreditsModal = ({ show, onClose, addons }: BuyCreditsModalProps) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);
  // Default to the middle pack as the recommended amount (mirrors the highlighted option pattern).
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(() => {
    const pkgs = addons.packages;

    return pkgs.length ? pkgs[Math.floor(pkgs.length / 2)].price_id : null;
  });
  const showRef = React.useRef(show);

  React.useEffect(() => {
    showRef.current = show;
    // The component stays mounted across open/close — reset so a reopen starts clean.
    if (!show) setLoadingPriceId(null);
  }, [show]);

  React.useEffect(() => {
    // Back-navigation from Stripe can restore the page from bfcache with JS state intact,
    // which would leave every Buy button stuck behind the pre-redirect loading state.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setLoadingPriceId(null);
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const handleBuy = async (pkg: IPaymentAddonItem) => {
    if (loadingPriceId) return;

    setLoadingPriceId(pkg.price_id);
    trackEvent(EVENTS.BUY_CREDITS.CHECKOUT_START, { credits: pkg.credits, price: pkg.price });

    try {
      const res = await paymentService.startAddonCheckout(pkg.price_id);

      // User closed the modal while the request was in flight — don't redirect or toast.
      if (!showRef.current) {
        setLoadingPriceId(null);

        return;
      }

      // 401/403/409/503 resolve with responseData: null + _handled: true and the interceptor
      // has already run the UX (logout / its own snackbar) — only reset state (CLAUDE.md Trap #1).
      if (res?._handled) {
        setLoadingPriceId(null);

        return;
      }

      const item = res?.responseData?.[0];

      if (item?.action === "checkout" && item.payment_link) {
        // Keep the button in its loading state while the browser navigates to Stripe.
        window.location.href = item.payment_link;

        return;
      }

      // The endpoint answers HTTP 200 even on failure — eligibility errors arrive as
      // item-level error_code (403 not admin / no paid plan, 404 unknown package).
      if (item?.error_code === 403) {
        showError(t("account:buy_credits.error_not_allowed"));
      } else if (item?.error_code === 404) {
        showError(t("account:buy_credits.error_not_found"));
      } else {
        showError(t("account:buy_credits.error_generic"));
      }
      setLoadingPriceId(null);
    } catch (error: any) {
      // 402 rejections already dispatched the paywall snackbar in the interceptor.
      if (showRef.current && error?.response?.status !== 402) {
        showError(t("account:buy_credits.error_generic"));
      }
      setLoadingPriceId(null);
    }
  };

  const selectedPkg = addons.packages.find((p) => p.price_id === selectedPriceId) ?? null;

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={t("account:buy_credits.title")}
      showCloseButton
      bodyClassName="!max-w-[600px] !w-full"
      footer={
        <Button className="btn-primary-medium w-full" disabled={!selectedPkg || loadingPriceId !== null} onClick={() => selectedPkg && handleBuy(selectedPkg)}>
          {loadingPriceId ? t("account:buy_credits.buy_cta_loading") : t("account:buy_credits.buy_cta", { price: selectedPkg?.price ?? 0 })}
        </Button>
      }
    >
      <div className="flex flex-col gap-5 p-5">
        <span className="text-bodyMd text-grey">{t("account:buy_credits.subtitle")}</span>

        <div className="flex flex-wrap gap-2.5">
          {addons.packages.map((pkg) => {
            const selected = pkg.price_id === selectedPriceId;

            return (
              <button
                key={pkg.price_id}
                type="button"
                onClick={() => setSelectedPriceId(pkg.price_id)}
                className={clsx(
                  "flex flex-1 min-w-[140px] flex-col items-center gap-1 rounded-md border p-4 text-center outline-none transition-colors",
                  selected ? "border-green bg-green/10" : "border-black-300 hover:border-grey"
                )}
              >
                <span className="text-h3 text-white">{pkg.credits.toLocaleString()}</span>
                <span className="text-bodySm text-grey">{t("account:buy_credits.pack_label")}</span>
                <span className="text-bodySm !font-semibold text-white mt-1">{t("account:buy_credits.pack_price", { price: pkg.price })}</span>
              </button>
            );
          })}
        </div>

        {addons.credits_expire_at && <span className="text-bodySm text-grey">{t("account:buy_credits.valid_until_note", { date: formatDate(addons.credits_expire_at, DateFormat.SHORT) })}</span>}
      </div>
    </Modal>
  );
};

export default BuyCreditsModal;
